import React, {
  useRef,
  useCallback,
  useMemo,
  ReactNode,
  createContext,
  useContext,
} from "react";
import { useParams } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import { useAccounts } from "../accounts/hooks/useAccounts";
import { JSONbig } from "../util/json";
import {
  Account,
  DecodableRequestInit,
  DecodableFetchOptions,
  ControlPlaneClient,
  NativeFetch,
} from "decodable-sdk-js/v1alpha2";

import { config, Config } from "../config";

// A React hook: Must be called from a component function,
// but not from a hook function (like useEffect) therein.
// See: https://reactjs.org/docs/hooks-rules.html
//
// Return a non-hook function that fetches (via `fetch()`), authorized via Auth0 token,
// JSON from the Decodable API.
// - Param path: Full API path; example: '/v1alpha2/accounts'
// - Param fetchArgs: optional object: merged with second arg to fetch()
//   - if fetchArgs.body is an object, JSON.stringify it and default fetchArgs.method to 'POST' and
//     fetchArgs.headers['Content-Type'] to 'application/json'.
// - Param opts: optional object: optional members:
//   - targetCell: string: Cell ID to target request to specific Cell
export function useDecodableApiFetch() {
  const { getAccessTokenSilently } = useAuth();
  return useCallback(newDecodableApiFetch({ getAccessTokenSilently }), []);
}

// Capturing this value at module load time helps ensure a XSS attack
// cannot simply monkey-patch the browser fetch function to intercept
// future requests with users' auth tokens & other data.
const defaultFetch = globalThis.fetch;

export type DecodableApiFetchOptions = {
  getAccessTokenSilently(): Promise<string>;
  fetch?: NativeFetch;
};

export type DecodableApiFetch<R = unknown> = (
  path: string,
  requestInit?: DecodableRequestInit,
  opts?: DecodableFetchOptions
) => R;

// This decouples the fetch function from react hooks to make testing easier.
// Otherwise, you must wrap this hook in a component, and use that to interface with the hook, lest you get:
// "Invalid hook call. Hooks can only be called inside of the body of a function component."
export function newDecodableApiFetch({
  getAccessTokenSilently,
  fetch = defaultFetch,
}: DecodableApiFetchOptions) {
  const client = new ControlPlaneClient({
    fetch,
    getAccessToken: getAccessTokenSilently,
  });

  return async function decodableApiFetch<T = any>(
    path: string,
    requestInit: DecodableRequestInit = {},
    opts: DecodableFetchOptions = {}
  ) {
    const headers = new Headers(requestInit.headers);
    let body = requestInit.body;

    if (typeof body == "object" && !(body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(body);
    }

    const response = await client.fetch(`${makeBaseUrl(config, opts)}${path}`, {
      ...requestInit,
      method: requestInit.method || (body ? "POST" : "GET"),
      headers,
      body,
    });

    if (response.headers.get("Content-Type") === "application/json") {
      return JSONbig.parse(await response.text());
    } else if (
      response.headers.get("Content-Disposition")?.includes("attachment")
    ) {
      return response;
    } else {
      return (await response.text()) as T;
    }
  };
}

type BaseUrlStrategy = { accountName?: string; targetCell?: string };

export const AccountScopedFetchContext = createContext(null);

export function AccountScopedFetchProvider({
  children = null,
}: {
  children?: ReactNode;
}) {
  const { accountName } = useParams();
  const { accounts, refresh: refreshAccounts } = useAccounts();

  const baseUrlStrategyRef = useRef<BaseUrlStrategy>({
    accountName: accountName,
  });

  if (!accountName) {
    throw new Error(
      "Programmer Error: AccountScopedFetchProvider must be rendered in a route with an accountName parameter"
    );
  }

  const genericFetch = useDecodableApiFetch();

  const decodableApiFetch = useMemo(
    () =>
      async (
        path: string,
        args?: DecodableRequestInit,
        opts?: DecodableFetchOptions
      ) => {
        const baseUrlStrategy: BaseUrlStrategy = baseUrlStrategyRef.current;

        try {
          return await genericFetch(path, args, {
            ...baseUrlStrategy,
            ...(opts ?? {}),
          });
        } catch (originalErr) {
          // After the initial account creation, the account's subdomain is sometimes not immediately resolvable.
          // Here we work around this by falling back to the subdomain of the cell that hosts the account.

          if (
            !(originalErr instanceof TypeError) ||
            !!baseUrlStrategy.targetCell
          ) {
            // If this doesn't look like a possible DNS issue, or we're already using the targetCell, fail fast.
            throw originalErr;
          }

          let cell: string;
          try {
            cell = accounts?.find((a: Account) => a.name == accountName)?.cell;
            if (!cell) {
              cell = (await refreshAccounts()).data?.find(
                (a: Account) => a.name == accountName
              )?.cell;
            }
          } catch (accountsErr) {
            // Ignore: We'll throw the orignal error below if we failed to get the cell for any reason.
            console.error(
              "Failed to read the accounts list (ignoring)",
              accountsErr
            );
          }

          if (!cell) {
            throw originalErr;
          }

          // Update the baseUrlStrategyRef so that we use the cell's subdomain by default going forward.
          baseUrlStrategyRef.current = { targetCell: cell };
          return genericFetch(path, args, {
            ...baseUrlStrategyRef.current,
            ...(opts ?? {}),
          });
        }
      },
    [genericFetch, accounts]
  );

  return (
    <AccountScopedFetchContext.Provider value={decodableApiFetch}>
      {children}
    </AccountScopedFetchContext.Provider>
  );
}

export function useAccountScopedFetch() {
  const decodableApiFetch = useContext(AccountScopedFetchContext);

  if (!decodableApiFetch) {
    throw new Error(
      "Programmer Error: useAccountScopedFetch may only be called in the context of an AccountScopedFetchProvider"
    );
  }

  return decodableApiFetch;
}

export type BaseUrlConfig = Pick<
  Config,
  | "DECODABLE_ENV"
  | "DECODABLE_CELL_API_URL_TEMPLATE"
  | "DECODABLE_ACCOUNT_API_URL_TEMPLATE"
>;

export type BaseUrlOptions = Pick<
  DecodableFetchOptions,
  "targetCell" | "accountName"
>;

export function makeBaseUrl(config: BaseUrlConfig, opts: BaseUrlOptions) {
  if (opts.accountName) {
    return makeAccountApiUrlBase(config, opts.accountName);
  }

  return makeCellApiUrlBase(config, opts.targetCell);
}

function makeCellApiUrlBase(config: BaseUrlConfig, targetCell?: string) {
  return config.DECODABLE_CELL_API_URL_TEMPLATE.replace(
    "{}",
    targetCell || config.DECODABLE_ENV
  );
}

function makeAccountApiUrlBase(config: BaseUrlConfig, accountName: string) {
  return config.DECODABLE_ACCOUNT_API_URL_TEMPLATE.replace("{}", accountName);
}
